/**
 * useAiCost — what an AI action costs, measured, shared across every control that can spend.
 *
 * WHY THIS IS A HOOK AND NOT A CONSTANT
 *
 * The previous answer was a hardcoded string on a button: "costs roughly two paise". The measured
 * mean was ₹2.70 — about 135x out — because the model, the prompt and the token counts all moved
 * and the label did not. Anything derived from a literal drifts the same way. This reads the
 * trailing median of real ledger rows, so the number cannot go stale without the spend going stale
 * with it.
 *
 * WHY IT IS MODULE-CACHED
 *
 * The verdict chip is mounted on six surfaces, several of them grids with a chip per row. A
 * per-component fetch would fire dozens of identical requests on a single page load. One in-flight
 * promise is shared by every consumer and refreshed on a timer.
 *
 * WHAT IT ALSO CARRIES
 *
 * `enabled` and `capReached`, so a control can render itself unavailable BEFORE a click rather
 * than after a wasted round-trip. That is what makes the Settings off switch visible rather than
 * merely enforced.
 */
import { useEffect, useState } from 'react';
import aiService, { type AiCostReport } from '../services/ai.service';
import { USD_TO_INR } from '../utils/constants';

/** Long enough that a grid of chips shares one fetch; short enough to follow a settings change. */
const TTL_MS = 5 * 60_000;

let cached: AiCostReport | null = null;
let cachedAt = 0;
let inFlight: Promise<AiCostReport | null> | null = null;
const listeners = new Set<(r: AiCostReport | null) => void>();

function load(force = false): Promise<AiCostReport | null> {
  if (!force && cached && Date.now() - cachedAt < TTL_MS) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = aiService.getCost()
    .then(r => {
      cached = r; cachedAt = Date.now();
      listeners.forEach(fn => fn(r));
      return r;
    })
    .catch(() => cached)
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Call after anything that changes spend or config, so the hints do not lag a settings change. */
export function refreshAiCost(): void { void load(true); }

export interface AiCostHint {
  report: AiCostReport | null;
  loading: boolean;
  /** True when the layer is switched off in Settings — nothing can be spent. */
  disabled: boolean;
  /** True when today's cap is reached, so a click would be refused anyway. */
  capReached: boolean;
  /** Why a control is unavailable, in the words the tooltip should use. Null when it is usable. */
  unavailableReason: string | null;
}

export function useAiCost(): AiCostHint {
  const [report, setReport] = useState<AiCostReport | null>(cached);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let live = true;
    const onChange = (r: AiCostReport | null) => { if (live) setReport(r); };
    listeners.add(onChange);
    load().then(r => { if (live) { setReport(r); setLoading(false); } });
    const t = setInterval(() => load(true), TTL_MS);
    return () => { live = false; listeners.delete(onChange); clearInterval(t); };
  }, []);

  const disabled = report ? !report.guard.enabled : false;
  const capReached = report ? report.guard.capReached : false;
  return {
    report, loading, disabled, capReached,
    unavailableReason: disabled
      ? 'The AI layer is switched off, so nothing will be spent. Turn it on in Settings → AI.'
      : capReached
        ? `Today's AI spend cap of ${inr(report!.guard.capUsd)} has been reached, so no further ` +
          'calls will be made until tomorrow. Raise it in Settings → AI.'
        : null,
  };
}

/** USD → rupees, using the app-wide rate rather than a second one defined here. */
export const usdToInr = (usd: number): number => usd * USD_TO_INR;

/**
 * Format a USD amount as rupees for a button or a tooltip.
 *
 * Sub-rupee amounts keep two decimals (₹0.45 is meaningfully different from ₹0.05); anything above
 * a rupee is rounded, because ₹2.83 and ₹2.70 are the same decision and the false precision only
 * invites the reader to trust a median as an exact price.
 */
export function inr(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '—';
  const v = usdToInr(usd);
  if (v === 0) return '₹0';
  if (v < 1) return `₹${v.toFixed(2)}`;
  if (v < 100) return `₹${v.toFixed(1).replace(/\.0$/, '')}`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

/**
 * The label for a billed control: "≈ ₹2.70", or an honest blank when nothing has been measured.
 *
 * Returns null rather than a guess when there is no sample. A control with no history should say
 * nothing about its cost — inventing a plausible figure is exactly how the two-paise claim
 * survived four days of real spend that disagreed with it.
 */
export function costLabel(usd: number | null | undefined): string | null {
  return usd == null || !Number.isFinite(usd) || usd <= 0 ? null : `≈ ${inr(usd)}`;
}
